import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Avatar,
  Divider,
  CircularProgress,
  Tab,
  LinearProgress,
  Badge,
  Alert,
  Chip,
  FormGroup,
  FormControlLabel,
  MenuItem,
  IconButton,
  Pagination,
  List,
  ListItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  School as SchoolIcon,
  EmojiEvents as EmojiEventsIcon,
  Forum as ForumIcon,
  Quiz as QuizIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Palette as PaletteIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Bookmark as BookmarkIcon,
  MenuBook as MenuBookIcon,
  WorkspacePremium,
  Verified as VerifiedIcon,
  CollectionsBookmark as CollectionsBookmarkIcon,
  Replay as ReplayIcon,
  CheckCircle as CheckCircleIcon,
  Chat as ChatIcon,
  ThumbUp as ThumbUpIcon,
  Comment as CommentIcon,
  Route as RouteIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import {
  authAPI,
  achievementsAPI,
  testsAPI,
  coursesAPI,
  usersAPI,
  badgesAPI,
  badgeSeriesAPI,
  lessonsAPI,
  forumAPI,
  learningPathsAPI,
} from '../services/api';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import Card from '../components/UIKit/Card';
import Button from '../components/UIKit/Button';
import Tabs from '../components/UIKit/Tabs';
import TextField from '../components/UIKit/TextField';
import Select from '../components/UIKit/Select';
import Switch from '../components/UIKit/Switch';
import AchievementCard from '../components/AchievementCard';
import TestResultCard from '../components/TestResultCard';
import BadgeCard from '../components/BadgeCard';
import BadgeSeriesCard from '../components/BadgeSeriesCard';
import { useNamedTabs } from '../components/onboarding/hooks/useNamedTabs';
import { useOnboarding } from '../context/OnboardingContext';
import { CourseCard, LessonCard } from '../components';
import LearningPathCard from '../components/LearningPathCard';
import ActiveLearningPathCard from '../components/ActiveLearningPathCard';
import MandatoryPathCard from '../components/MandatoryPathCard';
import TopicCard from '../components/TopicCard';
import { getUserAvatarSrc } from '../utils/avatarUtils';

const Profile = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, updateProfile, updateUserSettings } = useAuth();
  const { changeLanguage, currentLanguage } = useLanguage();
  const { toggleTheme } = useTheme();
  const { t } = useTranslation(['profile', 'common']);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [achievements, setAchievements] = useState([]);
  const [userData, setUserData] = useState({
    username: '',
    email: '',
    bio: '',
    avatar: '',
  });
  const [courses, setCourses] = useState({
    inProgressCourses: [],
    notStartedCourses: [],
    completedCourses: [],
  });

  const [stats, setStats] = useState({ level: 1, xp: 0, progress: 0 });
  const [error, setError] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const fileInputRef = React.useRef(null);
  
  // User activity states
  const [userActivity, setUserActivity] = useState({
    topics: [],
    comments: [],
    likedTopics: [],
    statistics: { totalTopics: 0, totalComments: 0, totalLikedTopics: 0 }
  });
  const [userActivityStats, setUserActivityStats] = useState({ topics: 0, comments: 0 });
  const [userCounts, setUserCounts] = useState({ 
    enrolledCourses: 0, 
    completedLessons: 0, 
    completedCourses: 0,
    achievements: 0,
    commentsWritten: 0, 
    topicsCreated: 0 
  });
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [activityTab, setActivityTab] = useState(0);
  const [testResults, setTestResults] = useState([]);
  const [userSettings, setUserSettings] = useState({
    language: 'en',
    theme: 'dark',
    emailNotifications: true,
    profileVisibility: 'public',
    showProgress: true,
    showAchievements: true,
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [settingsChanged, setSettingsChanged] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarksPagination, setBookmarksPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 4,
  });
  const [bookmarksCourses, setBookmarksCourses] = useState([]);
  const [loadingBookmarksCourses, setLoadingBookmarksCourses] = useState(false);
  const [bookmarksCoursesPagination, setBookmarksCoursesPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 4,
  });

  // Add new loading states for various sections
  const [loadingAchievements, setLoadingAchievements] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingTestResults, setLoadingTestResults] = useState(false);
  const [loadingBookmarks, setLoadingBookmarks] = useState(false);

  // Состояние пагинации для тестов
  const [testPagination, setTestPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 4,
  });

  // Добавляем новое состояние для загрузки значков
  const [loadingBadges, setLoadingBadges] = useState(false);
  const [badges, setBadges] = useState([]);
  const [badgeSeries, setBadgeSeries] = useState([]);
  const [loadingSeries, setLoadingSeries] = useState(false);

  // Состояние для завершенных уроков
  const [completedLessons, setCompletedLessons] = useState([]);
  const [loadingCompletedLessons, setLoadingCompletedLessons] = useState(false);

  // Learning paths state
  const [activeLearningPath, setActiveLearningPath] = useState(null);
  const [myLearningPaths, setMyLearningPaths] = useState([]);
  const [loadingLearningPaths, setLoadingLearningPaths] = useState(false);
  const [nextCourse, setNextCourse] = useState(null);
  const [mandatoryQueue, setMandatoryQueue] = useState([]);

  // Состояние пагинации для завершенных уроков
  const [completedLessonsPagination, setCompletedLessonsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 4,
  });

  // --- Универсальные табы по имени ---
  const profileTabs = useMemo(() => [
    { name: 'learning_paths', label: t('profile:learning_paths'), icon: <RouteIcon /> },
    { name: 'courses', label: t('profile:my_courses'), icon: <SchoolIcon /> },
    {
      name: 'completed_lessons',
      label: t('profile:completed_lessons'),
      icon: <CheckCircleIcon />,
    },
    {
      name: 'saved_lessons',
      label: t('profile:saved_lessons'),
      icon: <BookmarkIcon />,
    },
    {
      name: 'saved_courses',
      label: t('profile:saved_courses'),
      icon: <BookmarkIcon />,
    },
    {
      name: 'achievements',
      label: t('profile:achievements'),
      icon: <EmojiEventsIcon />,
    },
    { name: 'badges', label: t('profile:badges'), icon: <VerifiedIcon /> },
    {
      name: 'badge_series',
      label: t('profile:badge_series'),
      icon: <CollectionsBookmarkIcon />,
    },
    { name: 'activity', label: t('profile:my_activity'), icon: <ForumIcon /> },
    { name: 'tests', label: t('profile:tests'), icon: <QuizIcon /> },
    { name: 'settings', label: t('profile:settings'), icon: <SettingsIcon /> },
  ], [t]);

  const [tabName, setTabName] = useNamedTabs(profileTabs, profileTabs[0].name);

  // --- Instance для программного управления табами (онбординг) ---
  const instanceRef = useRef();
  useImperativeHandle(
    instanceRef,
    () => ({
      setTab: (name) => {
        if (profileTabs.some((tab) => tab.name === name)) setTabName(name);
      },
      getTab: () => tabName,
    }),
    [profileTabs, setTabName, tabName]
  );

  // --- Регистрируем instance в контексте онбординга ---
  const { setPageRef } = useOnboarding();
  useEffect(() => {
    setPageRef(instanceRef.current);
    return () => setPageRef(null);
  }, [setPageRef, instanceRef]);

  useEffect(() => {
    // Загрузка текущих настроек пользователя
    if (user && user.settings) {
      setUserSettings({
        language: user.settings.language || currentLanguage || 'en',
        theme: user.settings.theme || 'light',
        emailNotifications:
          user.settings.emailNotifications !== undefined
            ? user.settings.emailNotifications
            : true,
        profileVisibility: user.settings.profileVisibility || 'public',
        showProgress:
          user.settings.showProgress !== undefined
            ? user.settings.showProgress
            : true,
        showAchievements:
          user.settings.showAchievements !== undefined
            ? user.settings.showAchievements
            : true,
      });
    }
  }, [user, currentLanguage]);

  const fetchUserData = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    try {
      setLoading(true);
      setUserData({
        username: user.userMeta?.fullName || '',
        email: user.userMeta?.email || '',
        bio: user.bio || '',
        avatar: user.userMeta?.avatar || '',
      });
      // Загрузка статистики пользователя
      try {
        setStats({
          level: user.level || 1,
          xp: user.experience || 0,
          progress: (user.experience || 0) - ((user.level || 1) - 1) * 100,
        });
      } catch (statsErr) {
        setStats({ level: 1, xp: 0, progress: 0 });
      }
      
      // Загрузка статистики пользователя (объединенная статистика)
      try {
        console.log('=== FETCHING USER STATISTICS ===');
        console.log('User ID:', user._id);
        console.log('API URL:', `/users/${user._id}/statistics`);
        
        const statisticsResponse = await usersAPI.getUserStatistics(user._id);
        console.log('=== USER STATISTICS RESPONSE ===');
        console.log('Response status:', statisticsResponse.status);
        console.log('Response data:', statisticsResponse.data);
        
        const statisticsData = statisticsResponse.data.data;
        console.log('Statistics data:', statisticsData);
        
        // Обновляем статистику активности форума
        setUserActivityStats({
          topics: statisticsData.user.forum.topicsCreated || 0,
          comments: statisticsData.user.forum.commentsWritten || 0,
        });
        
        // Обновляем общие счетчики пользователя
        setUserCounts({
          enrolledCourses: 0, // Не возвращается в новой API
          completedLessons: statisticsData.user.learning.completedLessons || 0,
          completedCourses: statisticsData.user.learning.completedCourses || 0,
          achievements: statisticsData.user.learning.achievements || 0,
          commentsWritten: statisticsData.user.forum.commentsWritten || 0,
          topicsCreated: statisticsData.user.forum.topicsCreated || 0,
        });
        
        console.log('User statistics set:', {
          topics: statisticsData.user.forum.topicsCreated || 0,
          comments: statisticsData.user.forum.commentsWritten || 0,
          completedLessons: statisticsData.user.learning.completedLessons || 0,
          completedCourses: statisticsData.user.learning.completedCourses || 0,
          achievements: statisticsData.user.learning.achievements || 0,
        });
      } catch (statisticsErr) {
        console.error('=== ERROR FETCHING USER STATISTICS ===');
        console.error('Error details:', statisticsErr);
        console.error('Error response:', statisticsErr.response);
        console.error('Error message:', statisticsErr.message);
        
        setUserActivityStats({ topics: 0, comments: 0 });
        setUserCounts({ 
          enrolledCourses: 0, 
          completedLessons: 0, 
          completedCourses: 0,
          achievements: 0,
          commentsWritten: 0, 
          topicsCreated: 0 
        });
      }
    } catch (err) {
      setError(t('profile:profile_load_error'));
      console.error('Ошибка при загрузке данных профиля:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, t]);



  // Загрузка завершенных уроков с учётом пагинации
  const fetchCompletedLessonsWithPagination = useCallback(async (page = 1) => {
    if (!user?._id) {
      console.log('Пользователь не авторизован, пропускаем загрузку завершенных уроков');
      return;
    }
    
    try {
      setLoadingCompletedLessons(true);
      
      const response = await usersAPI.getUserCompletedLessons(user._id, {
        page,
        limit: completedLessonsPagination.perPage,
      });
      
      setCompletedLessons(response.data.data || []);
      const pag = response.data.pagination || {};
      setCompletedLessonsPagination({
        currentPage: pag.currentPage || 1,
        totalPages: pag.totalPages || 1,
        totalItems: pag.totalItems || 0,
        perPage: pag.perPage || completedLessonsPagination.perPage,
      });
    } catch (error) {
      console.error('Ошибка при загрузке завершенных уроков:', error);
      console.error('Детали ошибки:', error.response?.data || error.message);
      setCompletedLessons([]);
      // Показываем уведомление об ошибке
      toast.error(t('profile:error_loading_completed_lessons'));
    } finally {
      setLoadingCompletedLessons(false);
    }
  }, [user?._id, completedLessonsPagination.perPage, t]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchUserData();
  }, [isAuthenticated, user, navigate, fetchUserData]);

  // Отдельная функция для загрузки ачивок
  const fetchAchievements = useCallback(async () => {
    if (!isAuthenticated || !user?._id) return;
    try {
      setLoadingAchievements(true);
      const achievementsRes = await achievementsAPI.getUserAchievements(
        user._id
      );
      setAchievements(achievementsRes.data || []);
    } catch (achieveErr) {
      console.error('Ошибка при загрузке достижений:', achieveErr);
      setAchievements([]);
    } finally {
      setLoadingAchievements(false);
    }
  }, [isAuthenticated, user?._id]);

  // Функция для загрузки активности пользователя
  const fetchUserActivity = useCallback(async () => {
    if (!isAuthenticated || !user?._id) {
      console.log('Not authenticated or no user ID:', { isAuthenticated, userId: user?._id });
      return;
    }
    try {
      setLoadingActivity(true);
      console.log('=== FETCHING USER ACTIVITY ===');
      console.log('User ID:', user._id);
      console.log('API URL:', `/api/forum/user/${user._id}/activity`);
      
      const response = await forumAPI.getUserActivity(user._id);
      console.log('=== USER ACTIVITY RESPONSE ===');
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);
      setUserActivity(response.data);
      
      // Update activity statistics using the new statistics endpoint
      try {
        const statisticsResponse = await usersAPI.getUserStatistics(user._id);
        const statisticsData = statisticsResponse.data.data;
        setUserActivityStats({
          topics: statisticsData.user.forum.topicsCreated || 0,
          comments: statisticsData.user.forum.commentsWritten || 0,
        });
      } catch (statsErr) {
        console.error('Error fetching statistics:', statsErr);
        // Fallback to counting from activity data
        const activityData = response.data;
        setUserActivityStats({
          topics: activityData.topics?.length || 0,
          comments: activityData.comments?.length || 0,
        });
      }
    } catch (err) {
      console.error('=== ERROR FETCHING USER ACTIVITY ===');
      console.error('Error details:', err);
      console.error('Error response:', err.response);
      console.error('Error message:', err.message);
      setUserActivity({
        topics: [],
        comments: [],
        likedTopics: [],
        statistics: { totalTopics: 0, totalComments: 0, totalLikedTopics: 0 }
      });
      
      // Reset activity statistics on error
      setUserActivityStats({ topics: 0, comments: 0 });
    } finally {
      setLoadingActivity(false);
    }
  }, [isAuthenticated, user?._id]);

  // Обработчик изменения вкладки активности
  const handleActivityTabChange = (event, newValue) => {
    setActivityTab(newValue);
  };

  // Функция для форматирования даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return t('common:just_now');
    } else if (diffInHours < 24) {
      return t('common:hours_ago', { count: diffInHours });
    } else if (diffInHours < 48) {
      return t('common:yesterday');
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return t('common:days_ago', { count: diffInDays });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value,
    });
  };

  const handleAvatarClick = () => {
    if (editMode) {
      fileInputRef.current.click();
    }
  };

  const handleAvatarChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (!file.type.startsWith('image/')) {
        toast.error(t('profile:avatar_invalid_type'));
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        // 5MB
        toast.error(t('profile:avatar_too_large'));
        return;
      }

      // Создаем временный URL для предпросмотра
      const previewUrl = URL.createObjectURL(file);

      setUserData({
        ...userData,
        avatar: previewUrl,
      });

      setAvatarFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Добавляем файл аватара в данные формы, если он был выбран
      const updatedData = {
        ...userData,
        avatarFile: avatarFile,
      };

      await updateProfile(updatedData);
      setEditMode(false);
      setAvatarFile(null); // Сбрасываем файл после успешного обновления
      toast.success(t('profile:profile_update_success'));
    } catch (error) {
      console.error('Ошибка при обновлении профиля:', error);
      toast.error(t('profile:profile_update_error'));
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabName(profileTabs[newValue].name);
  };

  const handleSettingsChange = (e) => {
    const { name, value, checked } = e.target;
    const newValue = e.target.type === 'checkbox' ? checked : value;

    setUserSettings({
      ...userSettings,
      [name]: newValue,
    });

    setSettingsChanged(true);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value,
    });
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const saveSettings = async () => {
    try {
      // Обновляем настройки пользователя через API
      await updateUserSettings({
        username: user.username,
        email: user.email,
        bio: user.bio,
        settings: userSettings,
      });

      // Если язык изменился, применяем его сразу
      if (user.settings?.language !== userSettings.language) {
        changeLanguage(userSettings.language);
      }

      // Если тема изменилась, применяем её сразу
      if (user.settings?.theme !== userSettings.theme) {
        toggleTheme(userSettings.theme);
      }

      setSettingsChanged(false);
      toast.success(t('profile:settings_saved_success'));
    } catch (error) {
      console.error('Ошибка при сохранении настроек:', error);
      toast.error(t('profile:settings_save_error'));
    }
  };

  const changePassword = async () => {
    // Проверка на соответствие паролей
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('profile:passwords_not_match'));
      return;
    }

    // Проверка на минимальную длину пароля
    if (passwordData.newPassword.length < 6) {
      toast.error(t('profile:password_length'));
      return;
    }

    try {
      // Отправка запроса на изменение пароля
      await authAPI.changePassword(passwordData);

      // Сброс полей ввода
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      toast.success(t('profile:password_changed'));
    } catch (error) {
      console.error('Ошибка при изменении пароля:', error);
      toast.error(error.response?.data?.message || t('profile:password_change_error'));
    }
  };

  const fetchBookmarks = useCallback(async (page = 1) => {
    try {
      setLoadingBookmarks(true);
      const response = await coursesAPI.getBookmarks({
        page,
        limit: bookmarksPagination.perPage,
        type: 'lesson',
      });
      setBookmarks(response.data?.bookmarks || []);
      const pag = response.data?.pagination || {};
      setBookmarksPagination({
        currentPage: pag.currentPage || 1,
        totalPages: pag.totalPages || 1,
        totalItems: pag.totalItems || 0,
        perPage: pag.perPage || bookmarksPagination.perPage,
      });
    } catch (error) {
      console.error('Ошибка при загрузке закладок:', error);
      toast.error(t('profile:error_loading_bookmarks'));
    } finally {
      setLoadingBookmarks(false);
    }
  }, [bookmarksPagination.perPage, t]);

  // Обработчик смены страницы для закладок
  const handleBookmarksPageChange = (event, page) => {
    setBookmarksPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const fetchBookmarksCourses = useCallback(async (page = 1) => {
    try {
      setLoadingBookmarksCourses(true);
      const response = await coursesAPI.getBookmarks({
        page,
        limit: bookmarksCoursesPagination.perPage,
        type: 'course',
      });
      setBookmarksCourses(response.data?.bookmarks || []);
      const pag = response.data?.pagination || {};
      setBookmarksCoursesPagination({
        currentPage: pag.currentPage || 1,
        totalPages: pag.totalPages || 1,
        totalItems: pag.totalItems || 0,
        perPage: pag.perPage || bookmarksCoursesPagination.perPage,
      });
    } catch (error) {
      toast.error(t('profile:error_loading_bookmarks'));
    } finally {
      setLoadingBookmarksCourses(false);
    }
  }, [bookmarksCoursesPagination.perPage, t]);

  // Обработчик смены страницы для закладок курсов
  const handleBookmarksCoursesPageChange = (event, page) => {
    setBookmarksCoursesPagination((prev) => ({ ...prev, currentPage: page }));
  };

  // Загрузка тестов с учётом пагинации
  const fetchTestResults = useCallback(async (page = 1) => {
    try {
      setLoadingTestResults(true);
      const response = await testsAPI.getUserResults({
        page,
        limit: testPagination.perPage,
      });
      setTestResults(response.data.results || []);
      const pag = response.data.pagination || {};
      setTestPagination({
        currentPage: pag.currentPage || 1,
        totalPages: pag.totalPages || 1,
        totalItems: pag.totalItems || 0,
        perPage: pag.perPage || testPagination.perPage,
      });
    } catch (error) {
      console.error('Ошибка при загрузке результатов тестов:', error);
      setTestResults([]);
    } finally {
      setLoadingTestResults(false);
    }
  }, [testPagination.perPage]);

  // Обработчик смены страницы
  const handleTestPageChange = (event, page) => {
    setTestPagination((prev) => ({ ...prev, currentPage: page }));
  };

  // Обработчик смены страницы для завершенных уроков
  const handleCompletedLessonsPageChange = (event, page) => {
    setCompletedLessonsPagination((prev) => ({ ...prev, currentPage: page }));
  };

  // Функция для получения значков пользователя
  const fetchBadges = useCallback(async () => {
    try {
      const response = await badgesAPI.getUserBadges(user._id);
      setBadges(response.data);
    } catch (error) {
      console.error('Ошибка при получении значков:', error);
    }
  }, [user._id]);

  // Функция для получения серий значков пользователя
  const fetchBadgeSeries = useCallback(async () => {
    try {
      setLoadingSeries(true);
      const response = await badgeSeriesAPI.getUserBadgeSeries(user._id);
      setBadgeSeries(response.data.seriesProgress);
    } catch (error) {
      console.error('Ошибка при получении серий значков:', error);
    } finally {
      setLoadingSeries(false);
    }
  }, [user._id]);

  // Функция для проверки и получения новых значков
  const checkBadges = async () => {
    setLoadingBadges(true);
    try {
      const response = await badgesAPI.check();
      const data = response.data;

      // Обновляем список значков
      await fetchBadges();
      await fetchBadgeSeries();

      // Показываем уведомление о результатах
      if (data.newBadges && data.newBadges.length > 0) {
        toast.success(t('profile:new_badges', { count: data.newBadges.length }));
      } else {
        toast.info(t('profile:no_new_badges'));
      }
    } catch (error) {
      console.error('Ошибка при проверке значков:', error);
      toast.error(t('profile:badge_error'));
    } finally {
      setLoadingBadges(false);
    }
  };

  useEffect(() => {
    if (tabName === 'saved_lessons' && isAuthenticated) {
      fetchBookmarks(bookmarksPagination.currentPage);
    }
  }, [tabName, isAuthenticated, bookmarksPagination.currentPage, fetchBookmarks]);

  useEffect(() => {
    if (tabName === 'saved_courses' && isAuthenticated) {
      fetchBookmarksCourses(bookmarksCoursesPagination.currentPage);
    }
  }, [tabName, isAuthenticated, bookmarksCoursesPagination.currentPage, fetchBookmarksCourses]);

  useEffect(() => {
    // Загружаем результаты тестов только при выборе вкладки "Тесты"
    if (tabName === 'tests' && isAuthenticated) {
      fetchTestResults(testPagination.currentPage);
    }
  }, [tabName, isAuthenticated, testPagination.currentPage, fetchTestResults]);

  useEffect(() => {
    // Загружаем завершенные уроки только при выборе вкладки "Завершенные уроки"
    if (tabName === 'completed_lessons' && isAuthenticated) {
      fetchCompletedLessonsWithPagination(completedLessonsPagination.currentPage);
    }
  }, [tabName, isAuthenticated, completedLessonsPagination.currentPage, fetchCompletedLessonsWithPagination]);

  useEffect(() => {
    if (tabName === 'achievements' && isAuthenticated) {
      fetchAchievements();
    }
  }, [tabName, isAuthenticated, fetchAchievements]);

  useEffect(() => {
    console.log('=== ACTIVITY TAB EFFECT ===');
    console.log('Tab name:', tabName);
    console.log('Is authenticated:', isAuthenticated);
    console.log('User ID:', user?._id);
    
    if (tabName === 'activity' && isAuthenticated) {
      console.log('Calling fetchUserActivity...');
      fetchUserActivity();
    }
  }, [tabName, isAuthenticated, fetchUserActivity, user?._id]);

  // Добавляем загрузку значков при переключении на вкладку "Значки"
  useEffect(() => {
    if (tabName === 'badges' && isAuthenticated) {
      fetchBadges();
    }
  }, [tabName, isAuthenticated, fetchBadges]);

  // Добавляем загрузку серий значков при переключении на вкладку "Серии значков"
  useEffect(() => {
    if (tabName === 'badge_series' && isAuthenticated) {
      fetchBadgeSeries();
    }
  }, [tabName, isAuthenticated, fetchBadgeSeries]);

  useEffect(() => {
    if (tabName === 'learning_paths' && isAuthenticated) {
      const fetchLearningPaths = async () => {
        setLoadingLearningPaths(true);
        try {
          const [activeRes, myRes, mandatoryRes] = await Promise.all([
            learningPathsAPI.getActive(),
            learningPathsAPI.getMy(),
            learningPathsAPI.getMandatoryQueue(),
          ]);
          const activePathDetails = activeRes.data?.activeLearningPath?.pathDetails || null;
          setActiveLearningPath(activePathDetails);
          setMyLearningPaths(myRes.data?.learningPaths || myRes.data || []);
          setMandatoryQueue(mandatoryRes.data?.queue || []);

          if (activePathDetails) {
            const nextRes = await learningPathsAPI.getNextCourses();
            const nextCourses = nextRes.data?.nextCourses || [];
            setNextCourse(nextCourses.find((c) => c.isNext)?.course || nextCourses[0]?.course || null);
          }
        } catch (err) {
          console.error('Error loading learning paths:', err);
        } finally {
          setLoadingLearningPaths(false);
        }
      };
      fetchLearningPaths();
    }
  }, [tabName, isAuthenticated]);

  useEffect(() => {
    if (tabName === 'courses' && isAuthenticated && user?._id) {
      const fetchCourses = async () => {
        setLoadingCourses(true);
        try {
          const response = await usersAPI.getUserCourses(user._id);
          const coursesData = response.data.courses || response.data;
          setCourses({
            inProgressCourses: coursesData.inProgressCourses || [],
            notStartedCourses: coursesData.notStartedCourses || [],
            completedCourses: coursesData.completedCourses || [],
          });
        } catch (err) {
          console.error('❌ Error loading user courses:', err);
          setCourses({ inProgressCourses: [], notStartedCourses: [], completedCourses: [] });
        } finally {
          setLoadingCourses(false);
        }
      };
      fetchCourses();
    }
  }, [tabName, isAuthenticated, user?._id]);

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 8 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ReplayIcon />}
            onClick={async () => {
              setError(null);
              await fetchUserData();
            }}
          >
            {t('common:actions.retry')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, my: 2 }}>
      <Box>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Левая колонка с информацией о пользователе */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: '12px',
              }}
              className="personal-info"
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mb: { xs: 2, sm: 3 },
                }}
              >
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    editMode && (
                      <EditIcon
                        color="primary"
                        sx={{
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          padding: '4px',
                          cursor: 'pointer',
                        }}
                      />
                    )
                  }
                >
                  <Avatar
                    src={getUserAvatarSrc(userData)}
                    sx={{
                      width: { xs: 100, sm: 120 },
                      height: { xs: 100, sm: 120 },
                      mb: { xs: 1, sm: 2 },
                      cursor: editMode ? 'pointer' : 'default',
                      border: avatarFile ? '2px solid #4caf50' : 'none',
                    }}
                    onClick={handleAvatarClick}
                  />
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={handleAvatarChange}
                  />
                </Badge>
                <Typography variant="h5" align="center" gutterBottom>
                  {userData.username}
                </Typography>
                {!editMode && (
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    align="center"
                  >
                    {userData.bio || t('profile:no_user_info')}
                  </Typography>
                )}
              </Box>

              {editMode ? (
                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label={t('profile:username')}
                    name="username"
                    value={userData.username}
                    onChange={handleChange}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label={t('profile:email')}
                    name="email"
                    value={userData.email}
                    onChange={handleChange}
                    margin="normal"
                    disabled
                  />
                  <TextField
                    fullWidth
                    label={t('profile:about_me')}
                    name="bio"
                    value={userData.bio}
                    onChange={handleChange}
                    margin="normal"
                    multiline
                    rows={4}
                  />
                  <Box sx={{ my: 2 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {avatarFile
                        ? t('profile:new_avatar_selected')
                        : t('profile:click_avatar_to_change')}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setEditMode(false);
                        setAvatarFile(null);
                        setUserData({
                          username: user.username || '',
                          email: user.email || '',
                          bio: user.bio || '',
                          avatar: user.avatar || '',
                        });
                      }}
                      sx={{ mr: 2 }}
                    >
                      {t('common:actions.cancel')}
                    </Button>
                    <Button type="submit" variant="contained">
                      {t('common:actions.save')}
                    </Button>
                  </Box>
                </form>
              ) : (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('profile:email')}:
                    </Typography>
                    <Typography variant="body1">{userData.email}</Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('profile:experience')}:
                      </Typography>
                      <Typography variant="body2">
                        {stats.xp} / {stats.level * 100} XP
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={stats.progress}
                      sx={{ mt: 1 }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    {/* Кастомный Chip для уровня с градиентным синим фоном из темы */}
                    <Chip
                      icon={<WorkspacePremium />}
                      label={`${t('profile:level')} ${stats.level}`}
                      variant="outlined"
                      color="primary"
                      sx={(theme) => ({
                        fontWeight: 600,
                        fontSize: 16,
                        px: 2,
                        borderRadius: '16px',
                        bgcolor: 'unset',
                        background: `linear-gradient(90deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.dark} 100%)`,
                        color: theme.palette.primary.contrastText,
                        border: `1.5px solid ${theme.palette.primary.main}`,
                        '.MuiChip-icon': {
                          ml: 0.5,
                          mr: 0.5,
                          color: theme.palette.primary.contrastText,
                        },
                      })}
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}
                  >
                    <Box sx={{ textAlign: 'center', flex: 1, minWidth: '120px' }}>
                      <Typography variant="h6" color="primary">
                        {userCounts.completedCourses}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('profile:completedCourses')}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', flex: 1, minWidth: '120px' }}>
                      <Typography variant="h6" color="primary">
                        {userCounts.completedLessons}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('profile:completedLessons')}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', flex: 1, minWidth: '120px' }}>
                      <Typography variant="h6" color="primary">
                        {userCounts.achievements}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('profile:achievements_count')}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', flex: 1, minWidth: '120px' }}>
                      <Typography variant="h6" color="primary">
                        {userCounts.topicsCreated}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('profile:topicsCreated')}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', flex: 1, minWidth: '120px' }}>
                      <Typography variant="h6" color="primary">
                        {userCounts.commentsWritten}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('profile:commentsWritten')}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<EditIcon />}
                      onClick={() => setEditMode(true)}
                      sx={{ mb: 2 }}
                    >
                      {t('profile:edit_profile')}
                    </Button>

                    <Button
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      startIcon={<VerifiedIcon />}
                      onClick={checkBadges}
                      disabled={loadingBadges}
                    >
                      {loadingBadges ? t('profile:checking_badges') : t('profile:check_badges')}
                    </Button>
                  </Box>
                </>
              )}
            </Paper>
          </Grid>

          {/* Правая колонка с информацией о курсах и достижениях */}
          <Grid item xs={12} md={9}>
            <Paper
              elevation={0}
              sx={{ p: { xs: 2, sm: 3 }, borderRadius: '12px' }}
              className="profile-info"
            >
              <Box
                sx={{
                  bgcolor: 'background.default',
                  borderRadius: '16px',
                  p: '4px',
                  mb: { xs: 2, sm: 3 },
                  width: '100%',
                  overflow: 'hidden',
                }}
              >
                <Tabs
                  value={profileTabs.findIndex((tab) => tab.name === tabName)}
                  onChange={handleTabChange}
                  aria-label="profile tabs"
                >
                  {profileTabs.map((tab, idx) => (
                    <Tab key={tab.name} icon={tab.icon} label={tab.label} />
                  ))}
                </Tabs>
              </Box>

              {/* Вкладка с дорожками обучения */}
              {tabName === 'learning_paths' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('profile:learning_paths')}
                  </Typography>

                  {loadingLearningPaths ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      {/* Active learning path */}
                      <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
                        {t('profile:active_learning_path')}
                      </Typography>
                      {activeLearningPath ? (
                        <Box sx={{ mb: 3 }}>
                          <ActiveLearningPathCard learningPath={activeLearningPath} nextCourse={nextCourse} />
                        </Box>
                      ) : (
                        <Box sx={{ mb: 3 }}>
                          <Alert severity="info" sx={{ mb: 2 }}>
                            {t('profile:no_active_path')}
                          </Alert>
                          <Button variant="contained" onClick={() => navigate('/learning-paths')}>
                            {t('profile:browse_paths')}
                          </Button>
                        </Box>
                      )}

                      {/* Mandatory queue */}
                      {mandatoryQueue.length > 0 && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle1">
                              {t('profile:mandatory_queue')}
                            </Typography>
                            <Chip
                              label={`${mandatoryQueue.filter(i => i.isCompleted).length} / ${mandatoryQueue.length}`}
                              color="warning"
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            {t('profile:mandatory_queue_info')}
                          </Alert>
                          <Grid container spacing={{ xs: 1, sm: 2 }}>
                            {mandatoryQueue.map((item, index) => (
                              <Grid item key={item._id} xs={12} sm={6}>
                                <MandatoryPathCard item={item} index={index} activeLearningPathId={activeLearningPath?._id} />
                              </Grid>
                            ))}
                          </Grid>
                        </>
                      )}

                      {/* All enrolled/started paths */}
                      {myLearningPaths.length > 0 && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle1" gutterBottom>
                            {t('profile:all_my_paths')}
                            <Typography component="span" variant="body2" color="textSecondary" sx={{ ml: 1, fontWeight: 'normal' }}>
                              ({myLearningPaths.length})
                            </Typography>
                          </Typography>
                          <Grid container spacing={{ xs: 1, sm: 2 }}>
                            {myLearningPaths.map((path) => (
                              <Grid item key={path._id} xs={12} sm={6}>
                                <LearningPathCard learningPath={path} />
                              </Grid>
                            ))}
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                </Box>
              )}

              {/* Вкладка с курсами */}
              {tabName === 'courses' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('profile:my_courses')}
                  </Typography>
                  
                  {loadingCourses ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : courses.inProgressCourses.length === 0 &&
                    courses.completedCourses.length === 0 &&
                    courses.notStartedCourses.length === 0 ? (
                    <Alert severity="info" sx={{ mb: { xs: 1, sm: 2 } }}>
                      {t('profile:no_courses')}
                    </Alert>
                  ) : (
                    <>
                      {courses.inProgressCourses.length > 0 && (
                        <>
                          <Typography
                            variant="subtitle1"
                            gutterBottom
                            sx={{ mt: { xs: 1, sm: 2 } }}
                          >
                            {t('profile:courses_in_progress')} 
                            <Typography 
                              component="span" 
                              variant="body2" 
                              color="textSecondary"
                              sx={{ ml: 1, fontWeight: 'normal' }}
                            >
                              ({courses.inProgressCourses.length})
                            </Typography>
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                            {courses.inProgressCourses.map((course) => (
                              <CourseCard
                                key={course._id}
                                course={course}
                                progress={course.userProgress?.progress || 0}
                              />
                            ))}
                          </Box>
                        </>
                      )}

                      {courses.completedCourses.length > 0 && (
                        <>
                          <Typography
                            variant="subtitle1"
                            gutterBottom
                            sx={{ mt: { xs: 1, sm: 2 } }}
                          >
                            {t('profile:completed_courses')} 
                            <Typography 
                              component="span" 
                              variant="body2" 
                              color="textSecondary"
                              sx={{ ml: 1, fontWeight: 'normal' }}
                            >
                              ({courses.completedCourses.length})
                            </Typography>
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                            {courses.completedCourses.map((course) => (
                              <CourseCard key={course._id} course={course} progress={100} />
                            ))}
                          </Box>
                        </>
                      )}

                      {courses.notStartedCourses.length > 0 && (
                        <>
                          <Typography
                            variant="subtitle1"
                            gutterBottom
                            sx={{ mt: { xs: 1, sm: 2 } }}
                          >
                            {t('profile:courses_not_started')} 
                            <Typography 
                              component="span" 
                              variant="body2" 
                              color="textSecondary"
                              sx={{ ml: 1, fontWeight: 'normal' }}
                            >
                              ({courses.notStartedCourses.length})
                            </Typography>
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                            {courses.notStartedCourses.map((course) => (
                              <CourseCard key={course._id} course={course} progress={0} />
                            ))}
                          </Box>
                        </>
                      )}
                    </>
                  )}
                  {!loadingCourses && (courses.inProgressCourses.length > 0 ||
                    courses.completedCourses.length > 0) && (
                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/courses')}
                        sx={{ px: 4 }}
                      >
                        {t('profile:find_new_courses')}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {/* Вкладка с сохраненными уроками */}
              {tabName === 'saved_lessons' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('profile:saved_lessons')}
                  </Typography>
                  
                  {loadingBookmarks ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : bookmarks.length === 0 ? (
                    <Alert severity="info" sx={{ mb: { xs: 1, sm: 2 } }}>
                      {t('profile:no_bookmarks')}
                    </Alert>
                  ) : (
                    <>
                      <Grid container spacing={2}>
                        {bookmarks.map((bookmark) => (
                          <Grid item xs={12} key={bookmark._id}>
                            <Card>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <Box display="flex" alignItems="flex-start" mb={1}>
                                    <MenuBookIcon sx={{ mr: 1, color: 'secondary.main' }} />
                                    <Typography variant="subtitle1" fontWeight="bold">
                                      {bookmark.lesson?.title || t('lessons:lessonDetailPage.errors.lessonNotFound')}
                                    </Typography>
                                  </Box>
                                  {bookmark.lesson?.duration && (
                                    <Typography variant="body2" color="textSecondary">
                                      {t('lessons:duration')}: {bookmark.lesson.duration} {t('common:time.minutes')}
                                    </Typography>
                                  )}
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    display="block"
                                    sx={{ mt: 1 }}
                                  >
                                    {t('lessons:card.created')}: {new Date(bookmark.createdAt).toLocaleDateString()}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Box display="flex" justifyContent="flex-end" height="100%" alignItems="flex-start">
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      sx={{ mr: 1 }}
                                      onClick={() => {
                                        const lessonSlug = bookmark.lesson?.slug || bookmark.lesson?._id;
                                        navigate(`/lessons/${lessonSlug}`);
                                      }}
                                    >
                                      {t('common:actions.view')}
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      onClick={async () => {
                                        await lessonsAPI.toggleBookmark(bookmark.lesson?._id);
                                        fetchBookmarks(bookmarksPagination.currentPage);
                                        toast.success(t('profile:bookmark_removed'));
                                      }}
                                    >
                                      {t('common:actions.delete')}
                                    </Button>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                      {bookmarksPagination.totalPages > 1 && (
                        <Box
                          sx={{
                            mt: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}
                        >
                          <Pagination
                            count={bookmarksPagination.totalPages}
                            page={bookmarksPagination.currentPage}
                            onChange={handleBookmarksPageChange}
                            color="primary"
                            showFirstButton
                            showLastButton
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 2 }}
                          >
                            {t('common:pagination.showing_results_simple')
                              .replace(
                                '$1',
                                (bookmarksPagination.currentPage - 1) *
                                  bookmarksPagination.perPage +
                                  1
                              )
                              .replace(
                                '$2',
                                Math.min(
                                  bookmarksPagination.currentPage *
                                    bookmarksPagination.perPage,
                                  bookmarksPagination.totalItems
                                )
                              )
                              .replace('$3', bookmarksPagination.totalItems)}
                          </Typography>
                        </Box>
                      )}
                      {bookmarksPagination.totalPages === 1 && bookmarksPagination.totalItems > 0 && (
                        <Box display="flex" justifyContent="flex-end" mt={2}>
                          <Typography variant="body2" color="text.secondary">
                            {t('common:pagination.showing_results_simple')
                              .replace('$1', 1)
                              .replace('$2', bookmarksPagination.totalItems)
                              .replace('$3', bookmarksPagination.totalItems)}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}

              {/* Вкладка с сохраненными курсами */}
              {tabName === 'saved_courses' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('profile:saved_courses')}
                  </Typography>
                  {loadingBookmarksCourses ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : bookmarksCourses.length === 0 ? (
                    <Alert severity="info" sx={{ mb: { xs: 1, sm: 2 } }}>
                      {t('profile:no_bookmarks_courses')}
                    </Alert>
                  ) : (
                    <>
                      <Grid container spacing={2}>
                        {bookmarksCourses.map((bookmark) => (
                          <Grid item xs={12} key={bookmark._id}>
                            <Card>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <Box display="flex" alignItems="flex-start" mb={1}>
                                    <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
                                    <Typography variant="subtitle1" fontWeight="bold">
                                      {bookmark.course?.title || t('courses:courseCard.errors.courseNotFound')}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    display="block"
                                    sx={{ mt: 1 }}
                                  >
                                    {t('lessons:card.created')}: {new Date(bookmark.createdAt).toLocaleDateString()}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Box display="flex" justifyContent="flex-end" height="100%" alignItems="center">
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      sx={{ mr: 1 }}
                                      onClick={() => navigate(`/courses/${bookmark.course?.slug || bookmark.course?._id}`)}
                                    >
                                      {t('common:actions.view')}
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      onClick={async () => {
                                        await coursesAPI.toggleBookmark(bookmark.course?._id);
                                        fetchBookmarksCourses(bookmarksCoursesPagination.currentPage);
                                        toast.success(t('profile:bookmark_removed'));
                                      }}
                                    >
                                      {t('common:actions.delete')}
                                    </Button>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                      {bookmarksCoursesPagination.totalPages > 1 && (
                        <Box
                          sx={{
                            mt: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}
                        >
                          <Pagination
                            count={bookmarksCoursesPagination.totalPages}
                            page={bookmarksCoursesPagination.currentPage}
                            onChange={handleBookmarksCoursesPageChange}
                            color="primary"
                            showFirstButton
                            showLastButton
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 2 }}
                          >
                            {t('common:pagination.showing_results_simple')
                              .replace(
                                '$1',
                                (bookmarksCoursesPagination.currentPage - 1) *
                                  bookmarksCoursesPagination.perPage +
                                  1
                              )
                              .replace(
                                '$2',
                                Math.min(
                                  bookmarksCoursesPagination.currentPage *
                                    bookmarksCoursesPagination.perPage,
                                  bookmarksCoursesPagination.totalItems
                                )
                              )
                              .replace('$3', bookmarksCoursesPagination.totalItems)}
                          </Typography>
                        </Box>
                      )}
                      {bookmarksCoursesPagination.totalPages === 1 && bookmarksCoursesPagination.totalItems > 0 && (
                        <Box display="flex" justifyContent="flex-end" mt={2}>
                          <Typography variant="body2" color="text.secondary">
                            {t('common:pagination.showing_results_simple')
                              .replace('$1', 1)
                              .replace('$2', bookmarksCoursesPagination.totalItems)
                              .replace('$3', bookmarksCoursesPagination.totalItems)}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}



              {/* Вкладка с завершенными уроками */}
              {tabName === 'completed_lessons' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('profile:completed_lessons')} 
                    {completedLessons.length > 0 && (
                      <Typography 
                        component="span" 
                        variant="body2" 
                        color="textSecondary"
                        sx={{ ml: 1, fontWeight: 'normal' }}
                      >
                        ({completedLessons.length})
                      </Typography>
                    )}
                  </Typography>
                  
                  {loadingCompletedLessons ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : completedLessons.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {t('profile:no_completed_lessons')}
                    </Alert>
                  ) : (
                    <>
                      <Grid container spacing={2}>
                        {completedLessons.map((lesson) => (
                          <Grid item xs={12} key={lesson._id}>
                            <LessonCard 
                              lesson={lesson} 
                              variant="fullWidth" 
                              showCourseInfo={true}
                              showModuleInfo={true}
                              showViewButton={true}
                              showAuthor={true}
                            />
                          </Grid>
                        ))}
                      </Grid>
                      
                      {/* Пагинация и диапазон для завершенных уроков */}

                      {completedLessonsPagination.totalPages > 1 && (
                        <Box
                          sx={{
                            mt: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}
                        >
                          <Pagination
                            count={completedLessonsPagination.totalPages}
                            page={completedLessonsPagination.currentPage}
                            onChange={handleCompletedLessonsPageChange}
                            color="primary"
                            showFirstButton
                            showLastButton
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 2 }}
                          >
                            {t('profile:pagination.showing_results_simple')
                              .replace(
                                '$1',
                                (completedLessonsPagination.currentPage - 1) *
                                  completedLessonsPagination.perPage +
                                  1
                              )
                              .replace(
                                '$2',
                                Math.min(
                                  completedLessonsPagination.currentPage *
                                    completedLessonsPagination.perPage,
                                  completedLessonsPagination.totalItems
                                )
                              )
                              .replace('$3', completedLessonsPagination.totalItems)}
                          </Typography>
                        </Box>
                      )}
                      {/* Если страниц одна, но есть элементы, тоже показываем диапазон */}
                      {completedLessonsPagination.totalPages === 1 &&
                        completedLessonsPagination.totalItems > 0 && (
                          <Box display="flex" justifyContent="flex-end" mt={2}>
                            <Typography variant="body2" color="text.secondary">
                              {t('profile:pagination.showing_results_simple')
                                .replace('$1', 1)
                                .replace('$2', completedLessonsPagination.totalItems)
                                .replace('$3', completedLessonsPagination.totalItems)}
                            </Typography>
                          </Box>
                        )}
                    </>
                  )}
                </Box>
              )}

              {/* Вкладка с достижениями */}
              {tabName === 'achievements' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('profile:achievements')}
                  </Typography>
                  
                  {loadingAchievements ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : achievements.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {t('profile:no_achievements')}
                    </Alert>
                  ) : (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns:
                          'repeat(auto-fit, minmax(260px, 1fr))',
                        gap: 3,
                      }}
                    >
                      {achievements.map((achievement) => (
                        <AchievementCard
                          achievement={achievement}
                          key={typeof achievement === 'string' ? achievement : achievement._id}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Вкладка со значками */}
              {tabName === 'badges' && (
                <>
                  <Typography variant="h6" gutterBottom>
                    {t('profile:badges')} ({badges.length})
                  </Typography>

                  {badges.length > 0 ? (
                    <Grid container spacing={2}>
                      {badges.map((badge) => (
                        <Grid item xs={12} sm={6} md={4} key={badge._id}>
                          <BadgeCard badge={badge} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t('profile:no_badges_yet')}
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<VerifiedIcon />}
                        onClick={checkBadges}
                        disabled={loadingBadges}
                        sx={{ mt: 2 }}
                      >
                        {loadingBadges
                          ? t('profile:checking_badges')
                          : t('profile:check_badges')}
                      </Button>
                    </Box>
                  )}
                </>
              )}

              {/* Вкладка с сериями значков */}
              {tabName === 'badge_series' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('profile:badge_series')} ({badgeSeries.length})
                  </Typography>

                  {loadingSeries ? (
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                    </Box>
                  ) : badgeSeries.length > 0 ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns:
                          'repeat(auto-fit, minmax(260px, 1fr))',
                        gap: 2,
                      }}
                    >
                      {badgeSeries.map((series) => (
                        <BadgeSeriesCard series={series} key={series._id} />
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t('profile:no_badge_series_yet')}
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<VerifiedIcon />}
                        onClick={checkBadges}
                        disabled={loadingBadges}
                        sx={{ mt: 2 }}
                      >
                        {loadingBadges
                          ? t('profile:checking_badges')
                          : t('profile:check_badges')}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {/* Вкладка с активностью */}
              {tabName === 'activity' && (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <ForumIcon sx={{ mr: 1 }} /> {t('profile:my_activity')}
                  </Typography>

                  {loadingActivity ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Box>
                      
                      {/* Вкладки активности */}
                      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Tabs value={activityTab} onChange={handleActivityTabChange}>
                          <Tab 
                            icon={<ChatIcon />} 
                            label={`${t('profile:my_topics')} (${userActivity.statistics.totalTopics})`} 
                          />
                          <Tab 
                            icon={<CommentIcon />} 
                            label={`${t('profile:my_comments')} (${userActivity.statistics.totalComments})`} 
                          />
                          <Tab 
                            icon={<ThumbUpIcon />} 
                            label={`${t('profile:liked_topics')} (${userActivity.statistics.totalLikedTopics})`} 
                          />
                        </Tabs>
                      </Box>

                      {/* Мои темы */}
                      {activityTab === 0 && (
                        <Box>
                          {userActivity.topics.length === 0 ? (
                            <Alert severity="info">
                              {t('profile:no_topics_created')}
                            </Alert>
                          ) : (
                            <Box>
                              {userActivity.topics.map((topic) => (
                                <TopicCard
                                  key={topic._id}
                                  topic={topic}
                                  onLike={(type, id, reactionData = {}) => {
                                    setUserActivity(prevActivity => {
                                      const updatedTopics = prevActivity.topics.map(t => {
                                        if (t._id !== topic._id) {
                                          return t;
                                        }

                                        if (type === 'topics') {
                                          return {
                                            ...t,
                                            likes: Array.isArray(reactionData.likes) ? reactionData.likes : t.likes,
                                            dislikes: Array.isArray(reactionData.dislikes) ? reactionData.dislikes : (t.dislikes || []),
                                          };
                                        }

                                        if (type === 'comments') {
                                          const commentId = reactionData.commentId || id;
                                          const updatedComments = (t.comments || []).map(comment => {
                                            if (comment._id === commentId) {
                                              return {
                                                ...comment,
                                                likes: Array.isArray(reactionData.likes) ? reactionData.likes : comment.likes,
                                                dislikes: Array.isArray(reactionData.dislikes) ? reactionData.dislikes : (comment.dislikes || []),
                                              };
                                            }
                                            return comment;
                                          });

                                          return {
                                            ...t,
                                            comments: updatedComments,
                                          };
                                        }

                                        return t;
                                      });

                                      const normalizeId = (value) => {
                                        if (!value) return '';
                                        return typeof value === 'string' ? value : value.toString();
                                      };

                                      const likedTopics = updatedTopics.filter(currentTopic => {
                                        if (!Array.isArray(currentTopic.likes) || !user?._id) {
                                          return false;
                                        }
                                        return currentTopic.likes.some(likeId => normalizeId(likeId) === user._id);
                                      });

                                      return {
                                        ...prevActivity,
                                        topics: updatedTopics,
                                        likedTopics,
                                        statistics: {
                                          ...prevActivity.statistics,
                                          totalLikedTopics: likedTopics.length,
                                        },
                                      };
                                    });
                                  }}
                                  onAddComment={async (topicId, commentData) => {
                                    try {
                                      const response = await forumAPI.addComment(topicId, commentData);
                                      
                                      // Обновляем локальное состояние
                                      setUserActivity(prevActivity => ({
                                        ...prevActivity,
                                        topics: prevActivity.topics.map(t => {
                                          if (t._id === topicId) {
                                            const currentComments = Array.isArray(t.comments) ? t.comments : [];
                                            const newCommentObj = response?.data ? {
                                              ...response.data,
                                              content: commentData.content,
                                            } : {
                                              _id: response?.data?._id || Date.now().toString(),
                                              content: commentData.content,
                                              author: {
                                                _id: user._id,
                                                username: user.username,
                                                avatar: user.avatar,
                                              },
                                              createdAt: new Date().toISOString(),
                                              likes: [],
                                              replies: [],
                                            };
                                            
                                            return {
                                              ...t,
                                              comments: [...currentComments, newCommentObj]
                                            };
                                          }
                                          return t;
                                        })
                                      }));
                                    } catch (err) {
                                      console.error('Error adding comment:', err);
                                      toast.error('Error adding comment');
                                    }
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* Мои комментарии */}
                      {activityTab === 1 && (
                        <Box>
                          {userActivity.comments.length === 0 ? (
                            <Alert severity="info">
                              {t('profile:no_comments_made')}
                            </Alert>
                          ) : (
                            <List>
                              {userActivity.comments.map((comment) => (
                                <ListItem 
                                  key={comment._id} 
                                  sx={{ 
                                    flexDirection: 'column', 
                                    alignItems: 'flex-start', 
                                    p: 2, 
                                    mb: 1, 
                                    bgcolor: 'background.paper', 
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    '&:hover': {
                                      bgcolor: 'action.hover'
                                    }
                                  }}
                                  onClick={() => {
                                    // Navigate to forum detail page with topic ID (comment belongs to a topic)
                                    navigate(`/forum/${comment.topicId}`, { state: { from: 'profile' } });
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, width: '100%' }}>
                                    <CommentIcon sx={{ mr: 1, color: 'secondary.main' }} />
                                    <Typography variant="body1" sx={{ flexGrow: 1 }}>
                                      "{comment.content.length > 100 ? comment.content.substring(0, 100) + '...' : comment.content}"
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {formatDate(comment.createdAt)}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 3 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      📝 {t('profile:in_topic')} "{comment.topicTitle}"
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      📚 {comment.courseTitle}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      👍 {comment.likes?.length || 0} {t('profile:likes')}
                                    </Typography>
                                  </Box>
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </Box>
                      )}

                      {/* Вподобані теми */}
                      {activityTab === 2 && (
                        <Box>
                          {userActivity.likedTopics.length === 0 ? (
                            <Alert severity="info">
                              {t('profile:no_topics_liked')}
                            </Alert>
                          ) : (
                            <List>
                              {userActivity.likedTopics.map((topic) => (
                                <ListItem key={topic._id} sx={{ flexDirection: 'column', alignItems: 'flex-start', p: 2, mb: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, width: '100%' }}>
                                    <ThumbUpIcon sx={{ mr: 1, color: 'success.main' }} />
                                    <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                                      "{topic.title}"
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {formatDate(topic.createdAt)}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 3 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      👤 {topic.author?.username || 'Unknown User'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      📚 {topic.course?.title || topic.lesson?.title || 'Unknown Course'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      💬 {topic.comments?.length || 0} {t('profile:comments')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      👍 {topic.likes?.length || 0} {t('profile:likes')}
                                    </Typography>
                                  </Box>
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </Box>
                      )}

                    </Box>
                  )}
                </Box>
              )}

              {/* Вкладка с тестами */}
              {tabName === 'tests' && (
                <Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <QuizIcon sx={{ mr: 1 }} /> {t('profile:test_results')}
                  </Typography>

                  {loadingTestResults ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : testResults.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {t('profile:no_tests')}
                    </Alert>
                  ) : (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => navigate('/test-results')}
                        >
                          {t('profile:detailed_statistics')}
                        </Button>
                      </Box>

                      <Typography variant="subtitle1" gutterBottom>
                        {t('profile:latest_results')}
                      </Typography>

                      <Grid container spacing={2}>
                        {testResults.map((result) => (
                          <Grid item xs={12} key={result._id}>
                            <TestResultCard
                              result={result}
                              onRetake={async (testId) => {
                                try {
                                  const testInfo = await testsAPI.getTestInfo(testId);
                                  const { courseSlug, moduleSlug, lessonSlug } = testInfo.data;
                                  const path = lessonSlug
                                    ? `/courses/${courseSlug}/modules/${moduleSlug}/lessons/${lessonSlug}/tests/${testId}`
                                    : `/courses/${courseSlug}/modules/${moduleSlug}/tests/${testId}`;
                                  navigate(path);
                                } catch (error) {
                                  toast.error(t('tests:errors.startTestFailed'));
                                }
                              }}
                              onViewDetails={(resultId) =>
                                navigate(`/test-results/${resultId}`)
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                      {/* Пагинация и диапазон */}
                      {testPagination.totalPages > 1 && (
                        <Box
                          sx={{
                            mt: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}
                        >
                          <Pagination
                            count={testPagination.totalPages}
                            page={testPagination.currentPage}
                            onChange={handleTestPageChange}
                            color="primary"
                            showFirstButton
                            showLastButton
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 2 }}
                          >
                            {t('profile:pagination.showing_results_simple')
                              .replace(
                                '$1',
                                (testPagination.currentPage - 1) *
                                  testPagination.perPage +
                                  1
                              )
                              .replace(
                                '$2',
                                Math.min(
                                  testPagination.currentPage *
                                    testPagination.perPage,
                                  testPagination.totalItems
                                )
                              )
                              .replace('$3', testPagination.totalItems)}
                          </Typography>
                        </Box>
                      )}
                      {/* Если страниц одна, но есть элементы, тоже показываем диапазон */}
                      {testPagination.totalPages === 1 &&
                        testPagination.totalItems > 0 && (
                          <Box display="flex" justifyContent="flex-end" mt={2}>
                            <Typography variant="body2" color="text.secondary">
                              {t('profile:pagination.showing_results_simple')
                                .replace('$1', 1)
                                .replace('$2', testPagination.totalItems)
                                .replace('$3', testPagination.totalItems)}
                            </Typography>
                          </Box>
                        )}
                    </>
                  )}
                </Box>
              )}

              {/* Вкладка с настройками */}
              {tabName === 'settings' && (
                <Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <SecurityIcon sx={{ mr: 1, fontSize: '1.2rem' }} />{' '}
                    {t('profile:security')}
                  </Typography>

                  <Grid container spacing={{ xs: 2, sm: 3 }}>
                    {/* Настройки интерфейса */}
                    <Grid item xs={12} md={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          mb: { xs: 1.5, sm: 2 },
                          borderRadius: '12px',
                        }}
                        className="interface"
                      >
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                        >
                          <PaletteIcon sx={{ mr: 1, fontSize: '1.2rem' }} />{' '}
                          {t('profile:interface_settings')}
                        </Typography>

                        <Box sx={{ mb: 2 }}>
                          <Select
                            label={t('profile:language')}
                            name="language"
                            value={userSettings.language}
                            onChange={handleSettingsChange}
                          >
                            <MenuItem value="ru">Русский</MenuItem>
                            <MenuItem value="en">English</MenuItem>
                          </Select>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Select
                            label={t('profile:theme')}
                            name="theme"
                            value={userSettings.theme}
                            onChange={handleSettingsChange}
                          >
                            <MenuItem value="light">{t('profile:light')}</MenuItem>
                            <MenuItem value="dark">{t('profile:dark')}</MenuItem>
                            <MenuItem value="system">{t('profile:system')}</MenuItem>
                          </Select>
                        </Box>
                      </Paper>

                      {/* Настройки безопасности */}
                      <Paper
                        elevation={0}
                        sx={{ p: 2, borderRadius: '12px' }}
                        className="security"
                      >
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                        >
                          <SecurityIcon sx={{ mr: 1, fontSize: '1.2rem' }} />{' '}
                          {t('profile:security')}
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                          <TextField
                            fullWidth
                            name="currentPassword"
                            label={t('profile:current_password')}
                            type={
                              passwordVisibility.currentPassword
                                ? 'text'
                                : 'password'
                            }
                            value={passwordData.currentPassword}
                            onChange={handlePasswordChange}
                            placeholder={t('profile:current_password')}
                            InputProps={{
                              endAdornment: (
                                <IconButton
                                  aria-label="toggle current password visibility"
                                  onClick={() =>
                                    togglePasswordVisibility('currentPassword')
                                  }
                                  edge="end"
                                >
                                  {passwordVisibility.currentPassword ? (
                                    <VisibilityOffIcon />
                                  ) : (
                                    <VisibilityIcon />
                                  )}
                                </IconButton>
                              ),
                            }}
                          />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                          <TextField
                            fullWidth
                            name="newPassword"
                            label={t('profile:new_password')}
                            type={
                              passwordVisibility.newPassword
                                ? 'text'
                                : 'password'
                            }
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            placeholder={t('profile:new_password')}
                            InputProps={{
                              endAdornment: (
                                <IconButton
                                  aria-label="toggle new password visibility"
                                  onClick={() =>
                                    togglePasswordVisibility('newPassword')
                                  }
                                  edge="end"
                                >
                                  {passwordVisibility.newPassword ? (
                                    <VisibilityOffIcon />
                                  ) : (
                                    <VisibilityIcon />
                                  )}
                                </IconButton>
                              ),
                            }}
                          />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                          <TextField
                            fullWidth
                            name="confirmPassword"
                            label={t('profile:confirm_password')}
                            type={
                              passwordVisibility.confirmPassword
                                ? 'text'
                                : 'password'
                            }
                            value={passwordData.confirmPassword}
                            onChange={handlePasswordChange}
                            placeholder={t('profile:confirm_password')}
                            InputProps={{
                              endAdornment: (
                                <IconButton
                                  aria-label="toggle confirm password visibility"
                                  onClick={() =>
                                    togglePasswordVisibility('confirmPassword')
                                  }
                                  edge="end"
                                >
                                  {passwordVisibility.confirmPassword ? (
                                    <VisibilityOffIcon />
                                  ) : (
                                    <VisibilityIcon />
                                  )}
                                </IconButton>
                              ),
                            }}
                          />
                        </Box>

                        <Button
                          variant="contained"
                          color="primary"
                          sx={{ mt: 2 }}
                          onClick={changePassword}
                          disabled={
                            !passwordData.currentPassword ||
                            !passwordData.newPassword ||
                            !passwordData.confirmPassword
                          }
                        >
                          {t('profile:change_password')}
                        </Button>
                      </Paper>
                    </Grid>

                    {/* Настройки приватности */}
                    <Grid item xs={12} md={6}>
                      <Paper
                        elevation={0}
                        sx={{ p: 2, mb: 2, borderRadius: '12px' }}
                        className="privacy"
                      >
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                        >
                          <VisibilityIcon sx={{ mr: 1, fontSize: '1.2rem' }} />{' '}
                          {t('profile:privacy_settings')}
                        </Typography>

                        <Box sx={{ mb: 2 }}>
                          <Select
                            label={t('profile:profile_visibility')}
                            name="profileVisibility"
                            value={userSettings.profileVisibility}
                            onChange={handleSettingsChange}
                          >
                            <MenuItem value="public">{t('profile:public')}</MenuItem>
                            <MenuItem value="friends">{t('profile:friends_only')}</MenuItem>
                            <MenuItem value="private">{t('profile:private')}</MenuItem>
                          </Select>
                        </Box>

                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={userSettings.showProgress}
                                onChange={handleSettingsChange}
                                name="showProgress"
                              />
                            }
                            label={t('profile:show_progress')}
                            sx={{ gap: 1, mb: 2 }}
                          />

                          <FormControlLabel
                            control={
                              <Switch
                                checked={userSettings.showAchievements}
                                onChange={handleSettingsChange}
                                name="showAchievements"
                              />
                            }
                            label={t('profile:show_achievements')}
                            sx={{ gap: 1 }}
                          />
                        </FormGroup>
                      </Paper>

                      {/* Настройки уведомлений */}
                      <Paper
                        elevation={0}
                        sx={{ p: 2, borderRadius: '12px' }}
                        className="profile-notifications"
                      >
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                        >
                          <NotificationsIcon
                            sx={{ mr: 1, fontSize: '1.2rem' }}
                          />{' '}
                          {t('profile:notifications')}
                        </Typography>

                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={userSettings.emailNotifications}
                                onChange={handleSettingsChange}
                                name="emailNotifications"
                              />
                            }
                            label={t('profile:email_notifications')}
                            sx={{ gap: 1 }}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1, mb: 2 }}
                          >
                            {t('profile:notifications_description')}
                          </Typography>
                        </FormGroup>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Box
                    sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={saveSettings}
                      disabled={!settingsChanged}
                    >
                      {t('profile:save_settings')}
                    </Button>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
});

export default Profile;
